'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2ChangedDomain } from '@/lib/pr-v2/realtime-shared';
import type { PayloadForEventType } from '@/lib/realtime/types';
import type { QueryKey } from '@tanstack/react-query';
import { useMutationState, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { invalidateRealtimeQueries } from './pr-v2-realtime-query-invalidation';
import { autoFixLoopStatusQueryKey } from '@/data/auto-fix-loop/use-auto-fix-loop-status';
import { prV2CommentCreateMutationKey, prV2CommentEditMutationKey, prV2CommentReplyMutationKey, prV2CommentResolveMutationKey, prV2ReactionMutationKey, prV2ReviewerMutationKey, prV2ReviewSubmitMutationKey } from '@/data/v2/pr/mutation-keys';
import { prV2ChecksQueryKey, prV2CodeChangesQueryKeyPrefix, prV2CommentsQueryKey, prV2CubicReviewSummaryQueryKey, prV2DiscussionQueryKey, prV2OrderedFilesQueryKeyPrefix, prV2ReviewsQueryKey, prV2ReviewThreadsQueryKey, prV2StackQueryKey, prV2SummaryQueryKey, prV2VersionsQueryKey } from '@/data/v2/pr/query-keys';
import { clientLogger } from '@/lib/client-logger';
import { mergeabilityStatusQueryKey } from '@/lib/hooks/mergeability-status/keys';
import { useRealtime } from '@/lib/hooks/use-realtime';
import { buildPrV2RealtimePrId } from '@/lib/pr-v2/realtime-shared';
import { REALTIME_KEYS } from '@/lib/realtime/types';
type PrV2RealtimeUpdatesProps = {
  owner: string;
  repo: string;
  number: string;
  installationId?: number | null;
};

// Keep delivery-id dedupe short so retried deliveries can invalidate again after backoff windows.
const DELIVERY_DEDUPE_TTL_MS = 120_000;
// Per-action allowance for skipping matching manual_mutation invalidations that are expected
// to be echoes of this tab's own optimistic updates. We keep this short so stale allowances
// are naturally pruned even if no further local mutation-state changes are observed.
const LOCAL_SKIP_BUDGET_TTL_MS = 30_000;
// When the Realtime transport itself is unhealthy, fall back to polling only the DB/KV-backed
// PR v2 query set and avoid repeatedly invalidating heavier code artifact queries.
const SUBSCRIPTION_ERROR_INVALIDATION_TTL_MS = 30_000;
const MANUAL_MUTATION_ACTIONS = {
  COMMENT_CREATED: 'comment_created',
  COMMENT_REPLY_CREATED: 'comment_reply_created',
  COMMENT_EDITED: 'comment_edited',
  COMMENT_RESOLVED: 'comment_resolved',
  COMMENT_UNRESOLVED: 'comment_unresolved',
  COMMENT_REACTION_CREATED: 'comment_reaction_created',
  COMMENT_REACTION_DELETED: 'comment_reaction_deleted',
  REVIEWERS_ADDED: 'reviewers_added',
  REVIEWERS_REMOVED: 'reviewers_removed',
  REVIEW_DISMISSED: 'review_dismissed',
  REVIEW_WITH_COMMENTS_SUBMITTED: 'review_with_comments_submitted'
} as const;
const OPTIMISTIC_MANUAL_MUTATION_ACTIONS = [MANUAL_MUTATION_ACTIONS.COMMENT_CREATED, MANUAL_MUTATION_ACTIONS.COMMENT_REPLY_CREATED, MANUAL_MUTATION_ACTIONS.COMMENT_EDITED, MANUAL_MUTATION_ACTIONS.COMMENT_RESOLVED, MANUAL_MUTATION_ACTIONS.COMMENT_UNRESOLVED, MANUAL_MUTATION_ACTIONS.COMMENT_REACTION_CREATED, MANUAL_MUTATION_ACTIONS.COMMENT_REACTION_DELETED, MANUAL_MUTATION_ACTIONS.REVIEWERS_ADDED, MANUAL_MUTATION_ACTIONS.REVIEWERS_REMOVED, MANUAL_MUTATION_ACTIONS.REVIEW_DISMISSED, MANUAL_MUTATION_ACTIONS.REVIEW_WITH_COMMENTS_SUBMITTED] as const;
type OptimisticManualMutationAction = (typeof OPTIMISTIC_MANUAL_MUTATION_ACTIONS)[number];
type LocalSkipBudgetEntry = {
  remaining: number;
  expiresAt: number;
};
type PendingCountsByAction = Record<OptimisticManualMutationAction, number>;
function isOptimisticManualMutationAction(action: string): action is OptimisticManualMutationAction {
  return OPTIMISTIC_MANUAL_MUTATION_ACTIONS.includes(action as OptimisticManualMutationAction);
}
function createEmptyPendingCountsByAction(): PendingCountsByAction {
  return {
    [MANUAL_MUTATION_ACTIONS.COMMENT_CREATED]: 0,
    [MANUAL_MUTATION_ACTIONS.COMMENT_REPLY_CREATED]: 0,
    [MANUAL_MUTATION_ACTIONS.COMMENT_EDITED]: 0,
    [MANUAL_MUTATION_ACTIONS.COMMENT_RESOLVED]: 0,
    [MANUAL_MUTATION_ACTIONS.COMMENT_UNRESOLVED]: 0,
    [MANUAL_MUTATION_ACTIONS.COMMENT_REACTION_CREATED]: 0,
    [MANUAL_MUTATION_ACTIONS.COMMENT_REACTION_DELETED]: 0,
    [MANUAL_MUTATION_ACTIONS.REVIEWERS_ADDED]: 0,
    [MANUAL_MUTATION_ACTIONS.REVIEWERS_REMOVED]: 0,
    [MANUAL_MUTATION_ACTIONS.REVIEW_DISMISSED]: 0,
    [MANUAL_MUTATION_ACTIONS.REVIEW_WITH_COMMENTS_SUBMITTED]: 0
  };
}
export function PrV2RealtimeUpdates(t0) {
  const $ = _c(110);
  const {
    owner,
    repo,
    number,
    installationId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t1 = buildPrV2RealtimePrId(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const prId = t1;
  let t2;
  if ($[4] !== owner) {
    t2 = owner.toLowerCase();
    $[4] = owner;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const normalizedOwner = t2;
  let t3;
  if ($[6] !== repo) {
    t3 = repo.toLowerCase();
    $[6] = repo;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const normalizedRepo = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Map();
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const seenDeliveryIdsRef = useRef(t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Map();
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const localSkipBudgetsRef = useRef(t5);
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = createEmptyPendingCountsByAction();
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const previousPendingCountsRef = useRef(t6);
  const lastSubscriptionErrorInvalidationAtRef = useRef(0);
  let t7;
  if ($[11] !== number || $[12] !== owner || $[13] !== repo) {
    t7 = prV2CommentCreateMutationKey(owner, repo, number);
    $[11] = number;
    $[12] = owner;
    $[13] = repo;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = {
      filters: {
        mutationKey: t7,
        status: "pending"
      }
    };
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const createPendingCount = useMutationState(t8).length;
  let t9;
  if ($[17] !== number || $[18] !== owner || $[19] !== repo) {
    t9 = prV2CommentReplyMutationKey(owner, repo, number);
    $[17] = number;
    $[18] = owner;
    $[19] = repo;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = {
      filters: {
        mutationKey: t9,
        status: "pending"
      }
    };
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const replyPendingCount = useMutationState(t10).length;
  let t11;
  if ($[23] !== number || $[24] !== owner || $[25] !== repo) {
    t11 = prV2CommentEditMutationKey(owner, repo, number);
    $[23] = number;
    $[24] = owner;
    $[25] = repo;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11) {
    t12 = {
      filters: {
        mutationKey: t11,
        status: "pending"
      }
    };
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const editPendingCount = useMutationState(t12).length;
  let t13;
  if ($[29] !== number || $[30] !== owner || $[31] !== repo) {
    t13 = prV2CommentResolveMutationKey(owner, repo, number);
    $[29] = number;
    $[30] = owner;
    $[31] = repo;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t13) {
    t14 = {
      filters: {
        mutationKey: t13,
        status: "pending"
      },
      select: _temp
    };
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const pendingResolveActions = useMutationState(t14);
  let t15;
  if ($[35] !== number || $[36] !== owner || $[37] !== repo) {
    t15 = prV2ReactionMutationKey(owner, repo, number);
    $[35] = number;
    $[36] = owner;
    $[37] = repo;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t15) {
    t16 = {
      filters: {
        mutationKey: t15,
        status: "pending"
      },
      select: _temp2
    };
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const pendingReactionActions = useMutationState(t16);
  let t17;
  if ($[41] !== number || $[42] !== owner || $[43] !== repo) {
    t17 = prV2ReviewerMutationKey(owner, repo, number);
    $[41] = number;
    $[42] = owner;
    $[43] = repo;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t17) {
    t18 = {
      filters: {
        mutationKey: t17,
        status: "pending"
      },
      select: _temp3
    };
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const pendingReviewerActions = useMutationState(t18);
  let t19;
  if ($[47] !== number || $[48] !== owner || $[49] !== repo) {
    t19 = prV2ReviewSubmitMutationKey(owner, repo, number);
    $[47] = number;
    $[48] = owner;
    $[49] = repo;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== t19) {
    t20 = {
      filters: {
        mutationKey: t19,
        status: "pending"
      }
    };
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const reviewSubmitPendingCount = useMutationState(t20).length;
  let nextCounts;
  if ($[53] !== createPendingCount || $[54] !== editPendingCount || $[55] !== pendingReactionActions || $[56] !== pendingResolveActions || $[57] !== pendingReviewerActions || $[58] !== replyPendingCount || $[59] !== reviewSubmitPendingCount) {
    nextCounts = createEmptyPendingCountsByAction();
    nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_CREATED] = createPendingCount;
    nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_REPLY_CREATED] = replyPendingCount;
    nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_EDITED] = editPendingCount;
    for (const resolveAction of pendingResolveActions) {
      if (resolveAction === true) {
        nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_RESOLVED] = nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_RESOLVED] + 1;
      } else {
        if (resolveAction === false) {
          nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_UNRESOLVED] = nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_UNRESOLVED] + 1;
        }
      }
    }
    for (const reactionAction of pendingReactionActions) {
      if (reactionAction === "create") {
        nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_REACTION_CREATED] = nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_REACTION_CREATED] + 1;
      } else {
        if (reactionAction === "delete") {
          nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_REACTION_DELETED] = nextCounts[MANUAL_MUTATION_ACTIONS.COMMENT_REACTION_DELETED] + 1;
        }
      }
    }
    for (const reviewerActions of pendingReviewerActions) {
      if (!Array.isArray(reviewerActions)) {
        continue;
      }
      for (const reviewerAction of reviewerActions) {
        if (isOptimisticManualMutationAction(reviewerAction)) {
          nextCounts[reviewerAction] = nextCounts[reviewerAction] + 1;
        }
      }
    }
    nextCounts[MANUAL_MUTATION_ACTIONS.REVIEW_WITH_COMMENTS_SUBMITTED] = reviewSubmitPendingCount;
    $[53] = createPendingCount;
    $[54] = editPendingCount;
    $[55] = pendingReactionActions;
    $[56] = pendingResolveActions;
    $[57] = pendingReviewerActions;
    $[58] = replyPendingCount;
    $[59] = reviewSubmitPendingCount;
    $[60] = nextCounts;
  } else {
    nextCounts = $[60];
  }
  const pendingCountsByAction = nextCounts;
  let t21;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = now => {
      for (const [action, budget] of localSkipBudgetsRef.current.entries()) {
        if (budget.expiresAt <= now || budget.remaining <= 0) {
          localSkipBudgetsRef.current.delete(action);
        }
      }
    };
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  const pruneExpiredLocalSkipBudgets = t21;
  let t22;
  let t23;
  if ($[62] !== pendingCountsByAction) {
    t22 = () => {
      const now_0 = Date.now();
      pruneExpiredLocalSkipBudgets(now_0);
      const previousCounts = previousPendingCountsRef.current;
      for (const action_0 of OPTIMISTIC_MANUAL_MUTATION_ACTIONS) {
        const delta = pendingCountsByAction[action_0] - previousCounts[action_0];
        if (delta <= 0) {
          continue;
        }
        const existingBudget = localSkipBudgetsRef.current.get(action_0);
        const baseRemaining = existingBudget && existingBudget.expiresAt > now_0 ? existingBudget.remaining : 0;
        localSkipBudgetsRef.current.set(action_0, {
          remaining: baseRemaining + delta,
          expiresAt: now_0 + LOCAL_SKIP_BUDGET_TTL_MS
        });
      }
      previousPendingCountsRef.current = {
        ...pendingCountsByAction
      };
    };
    t23 = [pendingCountsByAction];
    $[62] = pendingCountsByAction;
    $[63] = t22;
    $[64] = t23;
  } else {
    t22 = $[63];
    t23 = $[64];
  }
  useEffect(t22, t23);
  let t24;
  if ($[65] !== number || $[66] !== owner || $[67] !== repo) {
    t24 = () => [prV2SummaryQueryKey(owner, repo, number), prV2VersionsQueryKey(owner, repo, number), prV2DiscussionQueryKey(owner, repo, number), prV2ReviewsQueryKey(owner, repo, number), prV2CommentsQueryKey(owner, repo, number), prV2ReviewThreadsQueryKey(owner, repo, number), prV2ChecksQueryKey(owner, repo, number), prV2CubicReviewSummaryQueryKey(owner, repo, number), mergeabilityStatusQueryKey(owner, repo, number, "v2")];
    $[65] = number;
    $[66] = owner;
    $[67] = repo;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  const collectCoreQueryKeys = t24;
  let t25;
  if ($[69] !== number || $[70] !== owner || $[71] !== repo) {
    t25 = () => [prV2StackQueryKey(owner, repo, number)];
    $[69] = number;
    $[70] = owner;
    $[71] = repo;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  const collectStackQueryKeys = t25;
  let t26;
  if ($[73] !== number || $[74] !== owner || $[75] !== repo) {
    t26 = () => [prV2CodeChangesQueryKeyPrefix(owner, repo, number), prV2OrderedFilesQueryKeyPrefix(owner, repo, number)];
    $[73] = number;
    $[74] = owner;
    $[75] = repo;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  const collectCodeArtifactQueryKeys = t26;
  let t27;
  if ($[77] !== number || $[78] !== owner || $[79] !== repo) {
    t27 = domain => {
      switch (domain) {
        case "summary":
          {
            return [prV2SummaryQueryKey(owner, repo, number), prV2StackQueryKey(owner, repo, number), prV2VersionsQueryKey(owner, repo, number), mergeabilityStatusQueryKey(owner, repo, number, "v2")];
          }
        case "discussion":
          {
            return [prV2DiscussionQueryKey(owner, repo, number), prV2ReviewThreadsQueryKey(owner, repo, number)];
          }
        case "reviews":
          {
            return [prV2ReviewsQueryKey(owner, repo, number), prV2StackQueryKey(owner, repo, number), prV2CubicReviewSummaryQueryKey(owner, repo, number), mergeabilityStatusQueryKey(owner, repo, number, "v2")];
          }
        case "comments":
          {
            return [prV2CommentsQueryKey(owner, repo, number), prV2ReviewThreadsQueryKey(owner, repo, number), prV2CubicReviewSummaryQueryKey(owner, repo, number)];
          }
        case "checks":
          {
            return [prV2ChecksQueryKey(owner, repo, number), prV2StackQueryKey(owner, repo, number), mergeabilityStatusQueryKey(owner, repo, number, "v2")];
          }
        case "code":
          {
            return [prV2CodeChangesQueryKeyPrefix(owner, repo, number), prV2OrderedFilesQueryKeyPrefix(owner, repo, number)];
          }
        case "cubic_review":
          {
            return [prV2CubicReviewSummaryQueryKey(owner, repo, number)];
          }
        case "auto_fix":
          {
            return [autoFixLoopStatusQueryKey(owner, repo, number)];
          }
        default:
          {
            return [];
          }
      }
    };
    $[77] = number;
    $[78] = owner;
    $[79] = repo;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  const collectDomainQueryKeys = t27;
  let t28;
  if ($[81] !== number || $[82] !== owner || $[83] !== repo) {
    t28 = (webhookEvent, action_1) => {
      if (webhookEvent !== "pull_request") {
        return [];
      }
      if (["opened", "reopened", "closed", "ready_for_review", "converted_to_draft"].includes(action_1 ?? "")) {
        return [mergeabilityStatusQueryKey(owner, repo, number, "v2")];
      }
      return [];
    };
    $[81] = number;
    $[82] = owner;
    $[83] = repo;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  const collectEventSpecificQueryKeys = t28;
  let t29;
  if ($[85] !== queryClient) {
    t29 = (queryKeys, traceContext) => {
      const uniqueQueryKeys = new Map();
      for (const queryKey of queryKeys) {
        uniqueQueryKeys.set(JSON.stringify(queryKey), queryKey);
      }
      const invalidatedQueryKeys = [];
      for (const queryKey_0 of uniqueQueryKeys.values()) {
        invalidatedQueryKeys.push(JSON.stringify(queryKey_0));
        invalidateRealtimeQueries(queryClient, queryKey_0);
      }
      if (process.env.NODE_ENV !== "production") {
        clientLogger.info("PrV2RealtimeUpdates: invalidated query keys", {
          action: traceContext?.action ?? null,
          changedDomains: traceContext?.changedDomains ?? null,
          invalidatedQueryKeys,
          webhookEvent: traceContext?.webhookEvent ?? null
        });
      }
    };
    $[85] = queryClient;
    $[86] = t29;
  } else {
    t29 = $[86];
  }
  const invalidateQueryKeys = t29;
  let t30;
  if ($[87] !== collectCodeArtifactQueryKeys || $[88] !== collectCoreQueryKeys || $[89] !== collectDomainQueryKeys || $[90] !== collectEventSpecificQueryKeys || $[91] !== collectStackQueryKeys || $[92] !== invalidateQueryKeys || $[93] !== normalizedOwner || $[94] !== normalizedRepo || $[95] !== number) {
    t30 = event => {
      if (event.pr.owner.toLowerCase() !== normalizedOwner || event.pr.repo.toLowerCase() !== normalizedRepo || event.pr.number.toString() !== number) {
        return;
      }
      if (event.deliveryId) {
        const now_1 = Date.now();
        for (const [deliveryId, timestamp] of seenDeliveryIdsRef.current.entries()) {
          if (now_1 - timestamp > DELIVERY_DEDUPE_TTL_MS) {
            seenDeliveryIdsRef.current.delete(deliveryId);
          }
        }
        if (seenDeliveryIdsRef.current.has(event.deliveryId)) {
          return;
        }
        seenDeliveryIdsRef.current.set(event.deliveryId, now_1);
      }
      if (event.webhookEvent === "manual_mutation" && typeof event.action === "string") {
        const manualMutationAction = event.action;
        if (isOptimisticManualMutationAction(manualMutationAction)) {
          const now_2 = Date.now();
          pruneExpiredLocalSkipBudgets(now_2);
          const localBudget = localSkipBudgetsRef.current.get(manualMutationAction);
          if (localBudget && localBudget.remaining > 0 && localBudget.expiresAt > now_2) {
            const nextRemaining = localBudget.remaining - 1;
            if (nextRemaining <= 0) {
              localSkipBudgetsRef.current.delete(manualMutationAction);
            } else {
              localSkipBudgetsRef.current.set(manualMutationAction, {
                ...localBudget,
                remaining: nextRemaining
              });
            }
            return;
          }
        }
      }
      if (Array.isArray(event.changedDomains) && event.changedDomains.length > 0) {
        invalidateQueryKeys([...Array.from(new Set(event.changedDomains)).flatMap(domain_0 => collectDomainQueryKeys(domain_0)), ...collectEventSpecificQueryKeys(event.webhookEvent, event.action)], {
          webhookEvent: event.webhookEvent,
          action: event.action,
          changedDomains: event.changedDomains
        });
        return;
      }
      switch (event.webhookEvent) {
        case "pull_request":
          {
            invalidateQueryKeys([...collectCoreQueryKeys(), ...collectStackQueryKeys(), ...(["opened", "synchronize", "reopened", "closed"].includes(event.action) ? collectCodeArtifactQueryKeys() : []), ...collectEventSpecificQueryKeys(event.webhookEvent, event.action)], {
              webhookEvent: event.webhookEvent,
              action: event.action,
              changedDomains: event.changedDomains
            });
            return;
          }
        case "pull_request_review":
        case "check_run":
          {
            invalidateQueryKeys([...collectCoreQueryKeys(), ...collectStackQueryKeys()], {
              webhookEvent: event.webhookEvent,
              action: event.action,
              changedDomains: event.changedDomains
            });
            return;
          }
        case "pull_request_review_comment":
        case "pull_request_review_thread":
        case "issue_comment":
          {
            invalidateQueryKeys(collectCoreQueryKeys(), {
              webhookEvent: event.webhookEvent,
              action: event.action,
              changedDomains: event.changedDomains
            });
            return;
          }
        case "manual_sync":
          {
            invalidateQueryKeys([...collectCoreQueryKeys(), ...collectStackQueryKeys(), ...collectCodeArtifactQueryKeys()], {
              webhookEvent: event.webhookEvent,
              action: event.action,
              changedDomains: event.changedDomains
            });
            return;
          }
        default:
          {
            invalidateQueryKeys([...collectCoreQueryKeys(), ...collectStackQueryKeys(), ...collectCodeArtifactQueryKeys()], {
              webhookEvent: event.webhookEvent,
              action: event.action,
              changedDomains: event.changedDomains
            });
          }
      }
    };
    $[87] = collectCodeArtifactQueryKeys;
    $[88] = collectCoreQueryKeys;
    $[89] = collectDomainQueryKeys;
    $[90] = collectEventSpecificQueryKeys;
    $[91] = collectStackQueryKeys;
    $[92] = invalidateQueryKeys;
    $[93] = normalizedOwner;
    $[94] = normalizedRepo;
    $[95] = number;
    $[96] = t30;
  } else {
    t30 = $[96];
  }
  const handleEvent = t30;
  let t31;
  if ($[97] !== collectCoreQueryKeys || $[98] !== invalidateQueryKeys) {
    t31 = () => {
      const now_3 = Date.now();
      if (now_3 - lastSubscriptionErrorInvalidationAtRef.current < SUBSCRIPTION_ERROR_INVALIDATION_TTL_MS) {
        return;
      }
      lastSubscriptionErrorInvalidationAtRef.current = now_3;
      invalidateQueryKeys(collectCoreQueryKeys());
    };
    $[97] = collectCoreQueryKeys;
    $[98] = invalidateQueryKeys;
    $[99] = t31;
  } else {
    t31 = $[99];
  }
  const handleSubscriptionError = t31;
  let t32;
  bb0: {
    if (installationId == null) {
      t32 = prId;
      break bb0;
    }
    let t33;
    if ($[100] !== installationId) {
      t33 = installationId.toString();
      $[100] = installationId;
      $[101] = t33;
    } else {
      t33 = $[101];
    }
    let t34;
    if ($[102] !== prId || $[103] !== t33) {
      t34 = [prId, t33];
      $[102] = prId;
      $[103] = t33;
      $[104] = t34;
    } else {
      t34 = $[104];
    }
    t32 = t34;
  }
  const subscriptionIds = t32;
  const t33 = Boolean(owner && repo && number);
  let t34;
  if ($[105] !== handleEvent || $[106] !== handleSubscriptionError || $[107] !== subscriptionIds || $[108] !== t33) {
    t34 = {
      eventKey: REALTIME_KEYS.PR_V2_UPDATE,
      id: subscriptionIds,
      onEvent: handleEvent,
      onError: handleSubscriptionError,
      enabled: t33
    };
    $[105] = handleEvent;
    $[106] = handleSubscriptionError;
    $[107] = subscriptionIds;
    $[108] = t33;
    $[109] = t34;
  } else {
    t34 = $[109];
  }
  useRealtime(t34);
  return null;
}
function _temp3(mutation_1) {
  const variables = mutation_1.state.variables as {
    toAdd?: unknown;
    toRemove?: unknown;
    reviewId?: unknown;
  } | string | undefined;
  if (typeof variables === "string") {
    return [MANUAL_MUTATION_ACTIONS.REVIEWERS_ADDED];
  }
  if (!variables || typeof variables !== "object") {
    return [];
  }
  if ("reviewId" in variables && variables.reviewId != null) {
    return [MANUAL_MUTATION_ACTIONS.REVIEW_DISMISSED];
  }
  const actions = [];
  if (Array.isArray(variables.toAdd) && variables.toAdd.length > 0) {
    actions.push(MANUAL_MUTATION_ACTIONS.REVIEWERS_ADDED);
  }
  if (Array.isArray(variables.toRemove) && variables.toRemove.length > 0) {
    actions.push(MANUAL_MUTATION_ACTIONS.REVIEWERS_REMOVED);
  }
  return actions;
}
function _temp2(mutation_0) {
  return (mutation_0.state.variables as {
    action?: unknown;
  } | undefined)?.action ?? null;
}
function _temp(mutation) {
  return (mutation.state.variables as {
    resolve?: unknown;
  } | undefined)?.resolve ?? null;
}